<template>
  <div class="mobile-only:mb-[180px]">
    <BlokkliProvider
      v-if="term"
      v-slot="{ entity }"
      v-bind="term?.blokkliProps"
      :entity="term"
    >
      <div
        v-blokkli-droppable:field_header_media
        class="relative flex h-[330px] w-full items-center justify-center overflow-hidden bg-grey-dark-03 bg-[length:100%_100%] bg-repeat-x text-center uppercase md:h-[352px]"
        :class="{
          'text-white': !isRebranding,
          'text-black': isRebranding,
        }"
        :style="style"
      >
        <MediaImage
          v-if="entity?.headerBackgroundImage || term?.headerBackgroundImage"
          v-bind="entity?.headerBackgroundImage || term?.headerBackgroundImage"
          class="ratio-image !absolute h-full w-full"
          img-class="object-cover"
          eager
          preload
          :image-style="imageStyle"
        />
        <div class="relative max-w-full px-16">
          <div
            v-blokkli-editable:field_header_overline
            class="mb-8 text-xs/[24px] tracking-[0.96px] md:mb-16 md:text-base md:tracking-[1.28px]"
          >
            {{ entity?.overline || term?.overline }}
          </div>
          <h1
            v-if="term?.label"
            class="text-5xl/[56px] font-light md:text-7xl"
            :class="{
              '!font-bold': isRebranding,
            }"
          >
            {{ entity?.label || term?.label }}
          </h1>
        </div>
      </div>
      <BlokkliField :list="term?.paragraphs" name="field_paragraphs" />
      <CollectionSlider
        :title="$texts('exploreOurFamilies', 'Explore our families')"
      />
      <PDPCommerceInfo class="mt-[210px]" />
    </BlokkliProvider>
  </div>
</template>

<script lang="ts" setup>
import type { TaxonomyTermWatchCollectionFragment } from '#graphql-operations'

const { $texts } = useEasyTexts()
const isRebranding = await useFeatureFlag()

const route = useRoute()

const { data: query } = await useAsyncData(
  route.fullPath,
  () =>
    useCachedGraphqlQuery('routeWatchCollection', {
      path: route.fullPath,
    }).then((v) => {
      // overwrite the metatags since oris_hreflang token is not a good place to set the current query parameters and we need the "product-xxx" ones to add to the canonicals and hreflang tags.
      const hreflangsForNonDrupalRoute = v.data.hreflangsForNonDrupalRoute
      const dataRoute = v.data.route

      if (
        !dataRoute ||
        !('metatags' in dataRoute && Array.isArray(dataRoute.metatags))
      ) {
        return v.data
      }

      const metatags = dataRoute.metatags

      hreflangsForNonDrupalRoute?.forEach((element) => {
        const index = metatags.findIndex((el) => el.id === element.id)
        if (index !== -1) {
          metatags[index] = element
        } else {
          metatags.push(element)
        }
      })

      return v.data
    }),
  {
    watch: [route],
  },
)

// Handles redirects and metatags.
const { entity: term } = await useDrupalRoute<
  TaxonomyTermWatchCollectionFragment | undefined
>(query)

// show 404 page if watch collection page does not have any paragraphs
if (!term.value?.paragraphs?.length) {
  throw createError({ statusCode: 404, fatal: true })
}

const imageStyle = defineImageStyle({
  type: 'pictures',
  pictures: {
    sm: {
      width: 600,
      height: 400,
    },
    md: {
      width: 800,
      height: 432,
    },
    lg: {
      width: 1130,
      height: 432,
    },
    xl: {
      width: 1390,
      height: 432,
    },
    xxl: {
      width: 1920,
      height: 432,
    },
    xxxl: {
      width: 2560,
      height: 432,
    },
  },
})

// TODO: remove in ORIS-2834
const style = computed(() => {
  const backgroundImage = buildWatchCollectionBackground(
    term.value?.backgroundImage,
  )
  if (!backgroundImage) {
    return
  }
  return {
    backgroundImage: `url(${backgroundImage})`,
  }
})

const { trackPageView } = useAnalyticsTracking()
trackPageView()
</script>

<style lang="scss" scoped>
:deep(.paragraph),
.paragraph {
  @include paragraph-spacing;

  &:first-child {
    @apply my-112 md:my-[132px];
  }

  &:nth-child(2) {
    @apply mt-0;
  }
}
</style>
